import { useState } from "react";
import { AlignCenter, AlignLeft, Bold, Italic, RotateCcw, Underline } from "lucide-react";
import { OpCallout } from "@/components/op/OpCallout";
import { OpNowBar } from "@/components/op/OpNowBar";
import { OpRoleBadge } from "@/components/op/OpRoleBadge";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { OP_SURFACE } from "@/lib/opSurfaces";
import { cn } from "@/lib/utils";

const BOXES = [
  { id: "haslo", label: "hasło" },
  { id: "opis", label: "opis" },
  { id: "ikona", label: "ikona" },
] as const;

export function OpSlideEditDemo() {
  const [mode, setMode] = useState<"text" | "bg">("text");
  const [box, setBox] = useState<(typeof BOXES)[number]["id"]>("haslo");
  const [marker, setMarker] = useState(true);

  return (
    <div className="space-y-4">
      <OpCallout variant="help" title="Makieta edycji slajdu (SlideCompose)">
        Lewa: podgląd 4:5 (ciemne tło preview). Prawa: Tekst / Tło + CTA Zastosuj. Bez API.
      </OpCallout>

      <OpNowBar
        label="Popraw hasło na slajdzie 1, potem Zastosuj"
        detail="Żółty marker = house. Zakres tła osobno od tekstu."
      />

      <div className={cn("overflow-hidden", OP_SURFACE.work.box)}>
        <div className="flex flex-wrap items-center justify-between gap-2 border-b border-slate-200 px-4 py-3">
          <div>
            <h2 className="text-base font-semibold text-slate-900">Skład slajdu 1</h2>
            <p className="text-xs text-muted-foreground">Pełna edycja tekstu i tła. Live preview.</p>
          </div>
          <Button type="button" variant="outline" size="sm">
            Wróć do listy
          </Button>
        </div>

        <div className="grid gap-0 lg:grid-cols-[360px_1fr]">
          {/* Preview */}
          <aside className={cn("space-y-3 p-4 text-slate-100", OP_SURFACE.preview.box)}>
            <OpRoleBadge tone="preview" label="Podgląd · 4:5" />
            <div
              className="relative mx-auto w-full max-w-[360px] overflow-hidden rounded-lg border border-zinc-600 bg-[#f3e8d8]"
              style={{ aspectRatio: "4 / 5" }}
            >
              <div className="absolute inset-x-0 top-[12%] px-6">
                <p
                  className={cn(
                    "font-serif text-2xl font-bold leading-tight text-slate-900",
                    marker && "bg-yellow-200",
                  )}
                >
                  Przed wizytą przebierasz się trzy razy.
                </p>
              </div>
              <div className="absolute inset-x-0 top-[42%] px-6">
                <p className="text-sm leading-snug text-slate-800">
                  To nie moda. To lęk, że matka znów skomentuje ciało.
                </p>
              </div>
              <div className="absolute bottom-8 right-8 flex h-10 w-10 items-center justify-center rounded-full border-2 border-slate-800 text-lg">
                →
              </div>
              {/* FB crop guides */}
              <div className="pointer-events-none absolute inset-x-0 top-0 h-[12.5%] bg-black/35" />
              <div className="pointer-events-none absolute inset-x-0 bottom-0 h-[12.5%] bg-black/35" />
              <div className="pointer-events-none absolute inset-[12.5%] border border-dashed border-white/50" />
            </div>
            <label className="flex items-center gap-2 text-xs text-slate-200">
              <input type="checkbox" defaultChecked />
              Przyciągaj do środka
            </label>
          </aside>

          {/* Controls */}
          <div className="space-y-4 bg-slate-50 p-4">
            <div className="flex flex-wrap gap-2">
              <Button
                type="button"
                size="sm"
                variant={mode === "text" ? "brand" : "outline"}
                onClick={() => setMode("text")}
              >
                Tekst
              </Button>
              <Button
                type="button"
                size="sm"
                variant={mode === "bg" ? "brand" : "outline"}
                onClick={() => setMode("bg")}
              >
                Tło
              </Button>
            </div>

            <div className={cn("rounded-lg border-2 p-3", OP_SURFACE.params.box)}>
              <p className="text-xs font-bold uppercase tracking-wider text-blue-900">Zakres tła</p>
              <div className="mt-2 flex flex-wrap gap-2">
                <span className="rounded-md border border-blue-700 bg-blue-200 px-2.5 py-1 text-xs font-medium">
                  Tylko ten slajd
                </span>
                <span className="rounded-md border border-blue-300 bg-white/80 px-2.5 py-1 text-xs">
                  Wszystkie slajdy
                </span>
              </div>
            </div>

            {mode === "text" ? (
              <div className="space-y-3">
                <div className="flex flex-wrap gap-1.5">
                  {BOXES.map((b) => (
                    <button
                      key={b.id}
                      type="button"
                      onClick={() => setBox(b.id)}
                      className={cn(
                        "rounded-md border-2 px-2.5 py-1 text-xs font-semibold",
                        box === b.id
                          ? "border-brand bg-brand/15 text-brand"
                          : "border-slate-300 bg-white text-slate-700",
                      )}
                    >
                      {b.label}
                    </button>
                  ))}
                  <button type="button" className="rounded-md border border-dashed border-slate-400 px-2 py-1 text-xs">
                    + opis
                  </button>
                </div>

                <div className={cn("rounded-lg border-2 p-3", OP_SURFACE.work.box)}>
                  <div className="mb-2 flex flex-wrap gap-1">
                    <Button type="button" size="icon" variant="outline" className="h-8 w-8">
                      <Bold className="h-3.5 w-3.5" />
                    </Button>
                    <Button type="button" size="icon" variant="outline" className="h-8 w-8">
                      <Italic className="h-3.5 w-3.5" />
                    </Button>
                    <Button type="button" size="icon" variant="outline" className="h-8 w-8">
                      <Underline className="h-3.5 w-3.5" />
                    </Button>
                    <Button type="button" size="icon" variant="outline" className="h-8 w-8">
                      <AlignLeft className="h-3.5 w-3.5" />
                    </Button>
                    <Button type="button" size="icon" variant="outline" className="h-8 w-8">
                      <AlignCenter className="h-3.5 w-3.5" />
                    </Button>
                    <Button
                      type="button"
                      size="sm"
                      variant={marker ? "brand" : "outline"}
                      onClick={() => setMarker((v) => !v)}
                    >
                      M
                    </Button>
                    <Button type="button" size="sm" variant="outline">
                      K
                    </Button>
                    <Button type="button" size="sm" variant="ghost">
                      ×
                    </Button>
                  </div>
                  <p className="mb-1 text-xs text-muted-foreground">Kolor tekstu</p>
                  <div className="mb-3 flex gap-1.5">
                    {["#111", "#444", "#b5614e", "#fff", "#f8f5ef"].map((c) => (
                      <span
                        key={c}
                        className="h-6 w-6 rounded-full border border-slate-300"
                        style={{ background: c }}
                      />
                    ))}
                  </div>
                  <div
                    className="min-h-[110px] rounded-md border-2 border-brand bg-white p-3 text-base font-semibold"
                    contentEditable
                    suppressContentEditableWarning
                  >
                    Przed wizytą przebierasz się trzy razy.
                  </div>
                  <p className="mt-1 text-[11px] text-muted-foreground">Markdown w szkicu · Wielkość 42</p>
                  <label className="mt-2 flex items-center gap-2 text-xs">
                    <input type="checkbox" /> Panel pod tekstem
                  </label>
                </div>

                <details className={cn("rounded-lg border-2 p-3", OP_SURFACE.optional.box)}>
                  <summary className="cursor-pointer text-sm font-semibold text-violet-950">
                    Opcjonalne · Parametry na pozostałe slajdy
                  </summary>
                  <div className="mt-2 flex flex-wrap gap-2 text-xs">
                    {["Font", "Kolor", "Wielkość", "Umiejscowienie"].map((p) => (
                      <label key={p} className="flex items-center gap-1.5">
                        <input type="checkbox" defaultChecked={p !== "Umiejscowienie"} />
                        {p}
                      </label>
                    ))}
                  </div>
                  <Button type="button" size="sm" variant="outline" className="mt-2">
                    Zastosuj parametry…
                  </Button>
                </details>
              </div>
            ) : (
              <div className={cn("space-y-3 rounded-lg border-2 p-3", OP_SURFACE.params.box)}>
                <p className="text-xs font-bold uppercase tracking-wider text-blue-900">Tło</p>
                <label className="block text-xs font-semibold text-blue-950">Przybliżenie</label>
                <input type="range" min={0.6} max={2} step={0.1} defaultValue={1} className="w-full" />
                <div className="flex flex-wrap gap-2">
                  <Button type="button" size="sm" variant="outline">
                    Z biblioteki
                  </Button>
                  <Button type="button" size="sm" variant="outline">
                    Z rolki
                  </Button>
                  <Button type="button" size="sm" variant="outline">
                    Podmień PNG
                  </Button>
                </div>
                <textarea
                  className="min-h-[64px] w-full rounded-md border-2 border-blue-400 bg-white p-2 text-sm"
                  placeholder="np. ciepłe okno, miękkie światło…"
                />
                <div className="flex flex-wrap gap-2">
                  {["Tylko tło", "Tło + layout", "Tylko layout"].map((m, i) => (
                    <span
                      key={m}
                      className={cn(
                        "rounded-md border px-2 py-1 text-xs",
                        i === 0 ? "border-blue-700 bg-blue-200 font-medium" : "border-blue-300 bg-white/80",
                      )}
                    >
                      {m}
                    </span>
                  ))}
                </div>
                <Button type="button" variant="brand" size="sm">
                  Nowe tło Gemini (3 warianty)
                </Button>
                <div className="grid grid-cols-3 gap-2">
                  {[1, 2, 3].map((n) => (
                    <div
                      key={n}
                      className="aspect-square rounded-md border-2 border-blue-300 bg-gradient-to-br from-amber-100 to-rose-100"
                    />
                  ))}
                </div>
              </div>
            )}

            <div className="flex flex-wrap gap-2 border-t border-slate-200 pt-3">
              <Button type="button" variant="brand" size="lg">
                Zastosuj
              </Button>
              <Button type="button" variant="outline" size="lg">
                <RotateCcw className="mr-2 h-3.5 w-3.5" />
                Przywróć skład typu
              </Button>
              <Badge variant="secondary">box: {box}</Badge>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
